import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Sync a single query parameter with React state-like API.
 *
 * @param {string} key - Query string key to read/write.
 * @param {string|null} [defaultValue=null] - Fallback value when param is not present.
 * @returns {[string|null, function]} - [value, setValue] similar to useState.
 */
function useQueryParamState(key, defaultValue = null) {
    const [searchParams, setSearchParams] = useSearchParams();

    const value = searchParams.get(key) ?? defaultValue;

    const setValue = useCallback(
        (nextValue, options) => {
            const newParams = new URLSearchParams(searchParams);
            const resolvedValue =
                typeof nextValue === 'function' ? nextValue(value) : nextValue;

            if (resolvedValue === undefined || resolvedValue === null || resolvedValue === '') {
                newParams.delete(key);
            } else {
                newParams.set(key, String(resolvedValue));
            }

            setSearchParams(newParams, options);
        },
        [key, searchParams, setSearchParams, value]
    );

    return [value, setValue];
}

export default useQueryParamState;



